import axios from "axios";
import { ENDPOINT_WITHOUT_BEARER } from "./constants/config";
import { store } from "./store";

const ACCESS_TOKEN_KEY = 'leopart_access_token';
const REFRESH_TOKEN_KEY = 'leopart_refresh_token';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const AUTH_BASE = BASE_URL.endsWith('/api')
  ? BASE_URL.replace(/\/api$/, '') + '/api/auth'
  : BASE_URL + '/auth';

// Axios instance senza interceptor per le chiamate auth (evita loop)
const authAxios = axios.create();

function parseJwtPayload(token: string): any {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now() + 30000; // 30s buffer
}

// ── BroadcastChannel cross-tab (bug JWT01) ────────────────────────────────────
// Tab A ruota il token → posta i nuovi token sulle altre tab.
// Tab B riceve il messaggio prima di tentare il proprio refresh → aggiorna
// localStorage e non fa una seconda chiamata /auth/refresh con il vecchio token.
type AuthBroadcastMessage =
  | { type: 'tokens-rotated'; access: string; refresh: string }
  | { type: 'logout' };

const broadcastChannel: BroadcastChannel | null = (() => {
  try {
    return new BroadcastChannel('leopart-auth');
  } catch {
    // BroadcastChannel non disponibile (es. ambienti non-browser nei test)
    return null;
  }
})();

if (broadcastChannel) {
  broadcastChannel.onmessage = (ev: MessageEvent<AuthBroadcastMessage>) => {
    const msg = ev.data;
    if (msg.type === 'tokens-rotated') {
      localStorage.setItem(ACCESS_TOKEN_KEY, msg.access);
      localStorage.setItem(REFRESH_TOKEN_KEY, msg.refresh);
    } else if (msg.type === 'logout') {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  };
}

// ── Singleton refresh (dedup intra-tab) ───────────────────────────────────────
// Refresh queue: only one refresh at a time, others wait for the result
let refreshPromise: Promise<string | null> | null = null;
let isRedirecting = false;

function redirectToLogin() {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  broadcastChannel?.postMessage({ type: 'logout' } satisfies AuthBroadcastMessage);
  // replace() instead of href assignment: prevents the browser Back button
  // from returning to the protected route and triggering a new 401 loop.
  window.location.replace('/login');
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;
    try {
      const response = await authAxios.post(`${AUTH_BASE}/refresh`, { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      // Notifica le altre tab che i token sono stati ruotati
      broadcastChannel?.postMessage({
        type: 'tokens-rotated',
        access: accessToken,
        refresh: newRefreshToken,
      } satisfies AuthBroadcastMessage);
      return accessToken;
    } catch {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

axios.interceptors.request.use(
  async (request) => {
    if (isRedirecting) return Promise.reject(new Error("Redirecting to login"));

    const whitoutToken = ENDPOINT_WITHOUT_BEARER.some((endpoint) =>
      request.url?.includes(endpoint)
    );

    if (!whitoutToken) {
      let token = localStorage.getItem(ACCESS_TOKEN_KEY);

      if (token && isTokenExpired(token)) {
        token = await refreshAccessToken();
        if (!token) {
          redirectToLogin();
          return Promise.reject(new Error("Session expired"));
        }
      }

      if (token) {
        const selectedEntity = store.getState().me.selectedEntity;
        request.headers.Authorization = `Bearer ${token}`;
        if (selectedEntity?._id)
          request.headers["entity-id"] = selectedEntity._id;
      }
    }

    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: retry once with refresh on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !isRedirecting && !originalRequest._retry) {
      originalRequest._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      }
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);
